import type { IDropdownOption } from "@fluentui/react";
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IconButton,
    Label,
    PrimaryButton,
    Separator,
    Stack,
    TextField,
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import React, { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import type {
    Node,
    Edge,
    ReactFlowInstance,
    Connection,
    NodeTypes,
    EdgeTypes,
    FitViewOptions,
    NodeChange,
    EdgeChange,
} from "@xyflow/react";
import {
    ReactFlow,
    Panel,
    addEdge,
    Background,
    Controls,
    useNodesState,
    useEdgesState,
} from "@xyflow/react";
import type { ActionTemplate, ActionTemplateCreate, ActionTemplateUpdate } from "../../api";
import {
    ActionsTemplatesApi,
    EloshieldModule,
    NodeKindDiscriminants as NodeKind,
    instanceOfActionTemplateCreate,
} from "../../api";
import VehiclesInZone from "../molecules/action-configs/vehicles-in-zone";
import PedestriansInZone from "../molecules/action-configs/pedestrians-in-zone";
import GreaterThan from "../molecules/action-configs/greater-than";
import StationsInZone from "../molecules/action-configs/stations-in-zone";
import Equal from "../molecules/action-configs/equal";
import And from "../molecules/action-configs/and";
import Or from "../molecules/action-configs/or";
import LessThan from "../molecules/action-configs/less-than";
import InErrorMode from "../molecules/action-configs/in-error-mode";
import ActionTemplateEdge from "../molecules/action-configs/action-config-edge";
import type { ValidationProblem } from "../../utils/validation";
import { ValidationStatus, wrapApiValidationRoute } from "../../utils/validation";
import Muting from "../molecules/action-configs/muting";
import { useTranslation } from "react-i18next";
import DigitalInputState from "../molecules/action-configs/digital-input-state";
import DeactivateAntennas from "../molecules/action-configs/deactivate-antennas";
import Not from "../molecules/action-configs/not";
import colors from "../../ui/colors.scss";
import ActionTemplateNodesSidebar from "./action-config-nodes-sidebar";
import AnyInZone from "../molecules/action-configs/any-in-zone";
import SetStationaryRelay from "../molecules/action-configs/set-stationary-relay";
import SetVehicleRelay from "../molecules/action-configs/set-vehicle-relay";
import SetStaticFlag from "../molecules/action-configs/set-static-flag";
import CheckFlag from "../molecules/action-configs/check-flag";
import Latch from "../molecules/action-configs/latch";
import SetDynamicFlag from "../molecules/action-configs/set-dynamic-flag";
import SetAndCheckStateVariable from "../molecules/action-configs/set-and-check-state-variable";
import Indications from "../molecules/action-configs/indications";
import SetSeatSensorMuting from "../molecules/action-configs/set-seat-sensor-muting";
import Timer from "../molecules/action-configs/timer";
import Pulse from "../molecules/action-configs/pulse";
import Sum from "../molecules/action-configs/sum";
import ObjectsInAntennaZone from "../molecules/action-configs/objects-in-antenna-zone";
import { ClearableDropdown } from "./clearable-dropdown";
import { FileQuestion, Forklift, RadioTower, UserRound } from "lucide-react";
import { JSONFileReader } from "./json-file-reader";
import { store } from "../../store";
import type { UUID } from "../../utils/uuid";
import { createUuid } from "../../utils/uuid";
import { observer } from "mobx-react";
import { PrimaryButtonValidation } from "./primary-button-validation";

/** All available node types. We use the OpenAPI generated enum variants as keys
    to ensure a direct mapping to the backend. */
const nodeTypes: NodeTypes = {
    // Nodes without input or output
    [NodeKind.SET_STATIC_FLAG]: SetStaticFlag,
    // Input nodes
    [NodeKind.CHECK_FLAG]: CheckFlag,
    [NodeKind.ANY_IN_ZONE]: AnyInZone,
    [NodeKind.DIGITAL_INPUT_STATE]: DigitalInputState,
    [NodeKind.IN_ERROR_MODE]: InErrorMode,
    [NodeKind.MUTING]: Muting,
    [NodeKind.PEDESTRIANS_IN_ZONE]: PedestriansInZone,
    [NodeKind.LATCH]: Latch,
    [NodeKind.STATIONS_IN_ZONE]: StationsInZone,
    [NodeKind.OBJECTS_IN_ANTENNA_ZONE]: ObjectsInAntennaZone,
    [NodeKind.VEHICLES_IN_ZONE]: VehiclesInZone,

    // Intermediate nodes
    [NodeKind.AND]: And,
    [NodeKind.EQUAL]: Equal,
    [NodeKind.GREATER_THAN]: GreaterThan,
    [NodeKind.LESS_THAN]: LessThan,
    [NodeKind.NOT]: Not,
    [NodeKind.OR]: Or,
    [NodeKind.SUM]: Sum,

    // Output nodes
    [NodeKind.DEACTIVATE_ANTENNAS]: DeactivateAntennas,
    [NodeKind.SET_DYNAMIC_FLAG]: SetDynamicFlag,
    [NodeKind.SET_STATIONARY_RELAY]: SetStationaryRelay,
    [NodeKind.SET_VEHICLE_RELAY]: SetVehicleRelay,
    [NodeKind.SET_AND_CHECK_STATE_VARIABLE]: SetAndCheckStateVariable,
    [NodeKind.TIMER]: Timer,
    [NodeKind.PULSE]: Pulse,
    [NodeKind.INDICATIONS]: Indications,
    [NodeKind.SET_SEAT_SENSOR_MUTING]: SetSeatSensorMuting,
};

const fitViewOptions: FitViewOptions = {
    padding: 0.2,
};

const edgeTypes: EdgeTypes = {
    actionTemplateEdge: ActionTemplateEdge,
    // Stay backwards compatible with old action configurations.
    actionConfigEdge: ActionTemplateEdge,
};

function getEloshieldModuleIcon(module: EloshieldModule): JSX.Element {
    switch (module) {
        case EloshieldModule.PEDESTRIAN:
            return <UserRound />;
        case EloshieldModule.STATION:
            return <RadioTower />;
        case EloshieldModule.VEHICLE:
            return <Forklift />;
        default:
            return <FileQuestion />;
    }
}

interface EloshieldModuleData {
    icon: string;
}

const onRenderOption = (option?: IDropdownOption<EloshieldModuleData>): JSX.Element => {
    if (!option) {
        return <></>;
    }
    return (
        <div style={{ display: "flex", alignItems: "center" }}>
            <div
                style={{
                    width: 14,
                    marginRight: 8,
                    display: "inline-flex",
                }}
            >
                {option.data?.icon}
            </div>
            <span>{option.text}</span>
        </div>
    );
};

const onRenderTitle = (options?: IDropdownOption<EloshieldModuleData>[]): JSX.Element => {
    if (!options) {
        return <></>;
    }

    const option = options[0];
    return (
        <div style={{ display: "flex", alignItems: "center" }}>
            <div
                style={{
                    width: 14,
                    marginRight: 8,
                    display: "inline-flex",
                }}
            >
                {option.data?.icon}
            </div>
            <span>{option.text}</span>
        </div>
    );
};

export const ActionConfiguration = observer((): JSX.Element => {
    const { t } = useTranslation();

    const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance>();
    const [actionTemplates, setActionTemplates] = useState<ActionTemplate[]>([]);
    const [selectedActionTemplate, setSelectedActionTemplate] = useState<ActionTemplate>();
    const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

    const [templateStart, setTemplateStart] = useState<UUID | undefined>();
    const [fileStart, { setFalse: unsetFileStart, setTrue: setFileStart }] = useBoolean(false);
    const [actionTemplateCreate, setActionTemplateCreate] = useState<ActionTemplateCreate>({
        eloshieldModule: EloshieldModule.VEHICLE,
        edges: [],
        nodes: [],
        label: "",
    });
    const [validationId, _] = useState(createUuid());

    const [hideCreateDialog, { toggle: toggleCreateHideDialog }] = useBoolean(true);
    const [hideEditDialog, { toggle: toggleEditHideDialog }] = useBoolean(true);
    const [hideDeleteDialog, { toggle: toggleDeleteHideDialog }] = useBoolean(true);

    const [modified, setModified] = useState(false);

    const actionsTemplatesApi = useMemo(() => new ActionsTemplatesApi(), []);

    const createDialogContentProps = {
        type: DialogType.normal,
        title: t("page.actionConfiguration.createDialog.title"),
        subText: t("page.actionConfiguration.createDialog.subText"),
    };

    const editDialogContentProps = {
        type: DialogType.normal,
        title: t("page.actionConfiguration.editDialog.title"),
        subText: t("page.actionConfiguration.editDialog.subText"),
    };

    const deleteDialogContentProps = {
        type: DialogType.normal,
        title: t("page.actionConfiguration.deleteDialog.title"),
        subText: t("page.actionConfiguration.deleteDialog.subText"),
    };

    const getId = useCallback(() => {
        // If there are no nodes just return ID 1.
        if (nodes.length === 0) {
            return 1;
        }
        // Otherwise return the next highest ID.
        const ids = nodes.map((node) => Number(node.id)).sort((n1, n2) => n1 - n2);
        return ids[ids.length - 1] + 1;
    }, [nodes]);

    // Initialize the validation model once.
    useEffect(() => {
        store.repositoryActionTemplates.validation.create.initializeModel(validationId, {
            eloshieldModule: EloshieldModule.VEHICLE,
            edges: [],
            nodes: [],
            label: "",
        });
    }, [validationId]);

    // Trigger validation on `actionTemplateCreate` change.
    useEffect(() => {
        store.repositoryActionTemplates.validation.create.updateModel(
            validationId,
            actionTemplateCreate,
        );
    }, [actionTemplateCreate, validationId]);

    useEffect(() => {
        actionsTemplatesApi
            .actionsTemplatesListActionTemplates({ pageSize: 500 })
            .then(setActionTemplates);
        reactFlowInstance?.fitView(fitViewOptions);
    }, [actionsTemplatesApi, selectedActionTemplate, reactFlowInstance]);

    const onConnect = useCallback(
        (conn: Connection) => {
            // Return early if source node output is incompatible with target
            // node input
            if (conn.sourceHandle !== conn.targetHandle) {
                return;
            }
            setEdges((eds) =>
                addEdge(
                    {
                        ...conn,
                        id: createUuid(),
                        animated: true,
                        type: "actionTemplateEdge",
                    },
                    eds,
                ),
            );
        },
        [setEdges],
    );

    const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    }, []);

    const onDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            if (!reactFlowInstance) {
                return;
            }

            // Check if the dropped element is valid
            const type = event.dataTransfer.getData("application/reactflow");
            if (typeof type === "undefined" || !type) {
                return;
            }

            const position = reactFlowInstance.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });
            const newNode: Node = {
                id: getId().toString(),
                type,
                position,
                deletable: true,
                data: {},
            };
            setNodes((nodes) => nodes.concat(newNode));
        },
        [getId, reactFlowInstance, setNodes],
    );

    async function validateTemplate(actionTemplateUpdate: ActionTemplateUpdate): Promise<boolean> {
        if (!selectedActionTemplate) {
            return false;
        }
        // Wrap API call to extract validation errors.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const remoteValidationResult = await wrapApiValidationRoute<any>(() =>
            actionsTemplatesApi.actionsTemplatesValidateUpdateActionTemplate({
                actionTemplateId: selectedActionTemplate.id,
                actionTemplateUpdate,
            }),
        );

        // TODO: Handle these edge errors, and show better errors for node errors.
        const invalidNodes = new Map<string, ValidationProblem>();
        const invalidEdges = new Map<string, ValidationProblem>();
        for (const key in remoteValidationResult.fields) {
            if (remoteValidationResult.fields[key]!.status != ValidationStatus.INVALID) {
                continue;
            }

            const problems: ValidationProblem[] = remoteValidationResult.fields[key]!.problems;
            problems.forEach((problem) => {
                if (key.startsWith("node-")) {
                    invalidNodes.set(key.substring("node-".length), problem);
                } else if (key.startsWith("edge-")) {
                    invalidEdges.set(key.substring("edge-".length), problem);
                }
            });
        }
        setNodes((nodes) =>
            nodes.map((node) => {
                const validationProblem = invalidNodes.get(node.id);
                if (validationProblem) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            invalid: true,
                            validationProblem: validationProblem,
                        },
                    };
                } else {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            invalid: false,
                            validationProblem: undefined,
                        },
                    };
                }
            }),
        );

        return Object.keys(remoteValidationResult.fields).length == 0;
    }

    async function updateAndValidateTemplate(): Promise<void> {
        if (!selectedActionTemplate) {
            return;
        }
        const actionTemplateUpdate: ActionTemplateUpdate = {
            ...selectedActionTemplate,
            nodes,
            edges,
        };
        if (await validateTemplate(actionTemplateUpdate)) {
            // Clear any previous validation errors before saving
            actionTemplateUpdate.nodes.forEach((node: Node) => {
                node.data.invalid = false;
                node.data.validationProblem = undefined;
            });

            await actionsTemplatesApi
                .actionsTemplatesUpdateActionTemplate({
                    actionTemplateId: selectedActionTemplate.id,
                    actionTemplateUpdate,
                })
                .then(() => setModified(false));
        }
    }

    function resetActionTemplate(): void {
        setSelectedActionTemplate(undefined);
        setNodes([]);
        setEdges([]);
    }

    function resetActionTemplateCreate(): void {
        setActionTemplateCreate({
            eloshieldModule: EloshieldModule.VEHICLE,
            nodes: [],
            edges: [],
            label: "",
        });
        unsetFileStart();
        setTemplateStart(undefined);
    }

    async function handleActionTemplateSelect(
        _event: FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
    ): Promise<void> {
        if (!option?.key) {
            return;
        }

        // We have to reset the action configuration to perform a clean state
        // update. If only the data field of a node changes the state would not
        // be propagated.
        resetActionTemplate();
        const actionTemplate = await actionsTemplatesApi.actionsTemplatesReadActionTemplate({
            actionTemplateId: option.key as string,
        });
        setSelectedActionTemplate(actionTemplate);

        // Reactflow v11 used width and height to store measured values, but in v12 these are now used to set overriding width and height.
        // We need to clear these values to prevent overflows.
        setNodes(
            actionTemplate.nodes.map((n: Node) => ({
                ...n,
                measured: undefined,
            })),
        );
        setEdges(actionTemplate.edges);
    }

    async function handleActionTemplateCreateBaseSelect(key?: string): Promise<void> {
        setTemplateStart(key);
        if (!key) {
            resetActionTemplateCreate();
            return;
        }

        const actionTemplate = await actionsTemplatesApi.actionsTemplatesReadActionTemplate({
            actionTemplateId: key,
        });
        setActionTemplateCreate(actionTemplate);
    }

    function exportActionTemplate(): void {
        if (!selectedActionTemplate) {
            return;
        }

        // Create a blob with the data.
        const jsonActionTemplate = JSON.stringify(selectedActionTemplate);
        const blob = new Blob([jsonActionTemplate], { type: "application/json" });

        // Create a temporary <a /> tag to download the object as a JSON file.
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = selectedActionTemplate.label;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(a.href);
    }

    const onNodesChangeCallback = useCallback(
        (changes: NodeChange<Node>[]) => {
            setModified(true);
            onNodesChange(changes);
        },
        [onNodesChange],
    );

    const onEdgesChangeCallback = useCallback(
        (changes: EdgeChange<Edge>[]) => {
            setModified(true);
            onEdgesChange(changes);
        },
        [onEdgesChange],
    );

    return (
        <>
            <ReactFlow
                proOptions={{ hideAttribution: true }}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChangeCallback}
                onEdgesChange={onEdgesChangeCallback}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                fitViewOptions={fitViewOptions}
            >
                <Panel position="top-center">
                    <Stack horizontal tokens={{ childrenGap: "1em" }}>
                        <Dropdown
                            label={t("page.actionConfiguration.dropdown.label")}
                            onRenderOption={onRenderOption}
                            onRenderTitle={onRenderTitle}
                            selectedKey={selectedActionTemplate?.id ?? null}
                            options={actionTemplates.map((config) => {
                                const icon = getEloshieldModuleIcon(config.eloshieldModule);
                                return {
                                    key: config.id,
                                    text: config.label,
                                    data: { icon: icon },
                                };
                            })}
                            onChange={handleActionTemplateSelect}
                            styles={{ dropdown: { width: 300 } }}
                        />
                        <Stack horizontal style={{ alignSelf: "end" }}>
                            <IconButton
                                iconProps={{ iconName: "Add" }}
                                onClick={toggleCreateHideDialog}
                            />
                            <IconButton
                                disabled={!selectedActionTemplate}
                                iconProps={{ iconName: "Edit" }}
                                onClick={toggleEditHideDialog}
                            />
                            <IconButton
                                disabled={!selectedActionTemplate}
                                iconProps={{ iconName: "Share" }}
                                onClick={exportActionTemplate}
                            />
                            <IconButton
                                disabled={!selectedActionTemplate}
                                iconProps={{
                                    iconName: "Trash",
                                    styles: {
                                        root: {
                                            color: selectedActionTemplate
                                                ? colors["secondarydashboard-severe"]
                                                : "",
                                        },
                                    },
                                }}
                                onClick={toggleDeleteHideDialog}
                            />
                            <PrimaryButton
                                disabled={!selectedActionTemplate || !modified}
                                onClick={updateAndValidateTemplate}
                                text={t("page.actionConfiguration.updateConfiguration")}
                            />
                        </Stack>
                    </Stack>
                </Panel>
                <Background />
                <Controls fitViewOptions={{ padding: 0.2 }} position={"top-left"} />
            </ReactFlow>
            <ActionTemplateNodesSidebar eloshieldModule={selectedActionTemplate?.eloshieldModule} />
            <Dialog
                hidden={hideDeleteDialog}
                onDismiss={toggleDeleteHideDialog}
                dialogContentProps={deleteDialogContentProps}
            >
                <DialogFooter>
                    <PrimaryButton
                        onClick={async () => {
                            if (!selectedActionTemplate) {
                                return;
                            }
                            await actionsTemplatesApi.actionsTemplatesDeleteActionTemplate({
                                actionTemplateId: selectedActionTemplate.id,
                            });
                            toggleDeleteHideDialog();
                            resetActionTemplate();
                        }}
                        text={t("page.actionConfiguration.deleteDialog.delete")}
                    />
                    <DefaultButton
                        onClick={toggleDeleteHideDialog}
                        text={t("page.actionConfiguration.deleteDialog.cancel")}
                    />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={hideEditDialog}
                onDismiss={toggleEditHideDialog}
                dialogContentProps={editDialogContentProps}
            >
                <TextField
                    label={t("page.actionConfiguration.editDialog.nameInput.label")}
                    onChange={(_e, newValue) => {
                        if (!selectedActionTemplate || !newValue) {
                            return;
                        }
                        setSelectedActionTemplate({
                            ...selectedActionTemplate,
                            label: newValue,
                        });
                    }}
                    placeholder={selectedActionTemplate?.label}
                />
                <DialogFooter>
                    <PrimaryButton
                        onClick={async () => {
                            if (!selectedActionTemplate) {
                                return;
                            }
                            const updatedTemplate =
                                await actionsTemplatesApi.actionsTemplatesUpdateActionTemplate({
                                    actionTemplateId: selectedActionTemplate.id,
                                    actionTemplateUpdate: selectedActionTemplate,
                                });
                            toggleEditHideDialog();
                            setSelectedActionTemplate(updatedTemplate);
                            setNodes(updatedTemplate.nodes);
                            setEdges(updatedTemplate.edges);
                        }}
                        text={t("page.actionConfiguration.editDialog.update")}
                    />
                    <DefaultButton
                        onClick={toggleEditHideDialog}
                        text={t("page.actionConfiguration.editDialog.cancel")}
                    />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={hideCreateDialog}
                onDismiss={toggleCreateHideDialog}
                dialogContentProps={createDialogContentProps}
            >
                <Dropdown
                    label={t("page.actionConfiguration.createDialog.moduleInput.label")}
                    onRenderOption={onRenderOption}
                    onRenderTitle={onRenderTitle}
                    selectedKey={actionTemplateCreate.eloshieldModule}
                    options={Object.values(EloshieldModule)
                        .filter((key) => key !== EloshieldModule.PEDESTRIAN)
                        .map((key, _index) => {
                            return {
                                key,
                                text: t(`component.actionConfigNodes.eloshieldModule.${key}`),
                                data: { icon: getEloshieldModuleIcon(key) },
                            };
                        })}
                    onChange={(_e, option) => {
                        if (!option) {
                            return;
                        }
                        setActionTemplateCreate((prev) => ({
                            ...prev,
                            eloshieldModule: option.key as EloshieldModule,
                        }));
                    }}
                    disabled={templateStart !== undefined || fileStart}
                    required
                />
                <Separator />
                <Stack>
                    <ClearableDropdown
                        label={t("page.actionConfiguration.createDialog.templateInput.label")}
                        onRenderOption={onRenderOption}
                        onRenderTitle={onRenderTitle}
                        selectedKey={templateStart}
                        options={actionTemplates.map((config) => {
                            const icon = getEloshieldModuleIcon(config.eloshieldModule);
                            return {
                                key: config.id,
                                text: config.label,
                                data: { icon: icon },
                            };
                        })}
                        onChange={handleActionTemplateCreateBaseSelect}
                        disabled={fileStart}
                    />
                    <Separator>{t("page.actionConfiguration.createDialog.or")}</Separator>
                    <Stack>
                        <Label styles={{ root: { paddingTop: 0 } }}>
                            {t("page.actionConfiguration.createDialog.fileInput.label")}
                        </Label>
                        <JSONFileReader
                            onJsonParsed={(actionTemplate?: ActionTemplate) => {
                                if (!actionTemplate) {
                                    return;
                                }
                                setActionTemplateCreate(actionTemplate);
                                setFileStart();
                            }}
                            validate={(data: object) => {
                                if (!instanceOfActionTemplateCreate(data)) {
                                    return false;
                                }
                                return true;
                            }}
                            onClear={() => {
                                resetActionTemplateCreate();
                                unsetFileStart();
                            }}
                            disabled={templateStart !== undefined}
                        />
                    </Stack>
                </Stack>
                <Separator />
                <TextField
                    label={t("page.actionConfiguration.createDialog.nameInput.label")}
                    onChange={(_e, newValue) => {
                        setActionTemplateCreate((prev) => ({
                            ...prev,
                            label: newValue ?? "",
                        }));
                    }}
                    value={actionTemplateCreate.label}
                    errorMessage={store.i18n.formatFieldValidationState(
                        store.repositoryActionTemplates.validation.create.getFieldValidationState(
                            validationId,
                            "label",
                        ),
                    )}
                    required
                />
                <DialogFooter>
                    <PrimaryButtonValidation
                        onClick={async () => {
                            if (!actionTemplateCreate) {
                                return;
                            }
                            const newActionTemplate =
                                await actionsTemplatesApi.actionsTemplatesCreateActionTemplate({
                                    actionTemplateCreate,
                                });
                            toggleCreateHideDialog();
                            resetActionTemplateCreate();
                            setSelectedActionTemplate(newActionTemplate);
                            setNodes(newActionTemplate.nodes);
                            setEdges(newActionTemplate.edges);
                        }}
                        text={t("page.actionConfiguration.createDialog.create")}
                        validation={store.repositoryActionTemplates.validation.create}
                        validationId={validationId}
                    />
                    <DefaultButton
                        onClick={toggleCreateHideDialog}
                        text={t("page.actionConfiguration.createDialog.cancel")}
                    />
                </DialogFooter>
            </Dialog>
        </>
    );
});
